import { useCallback, useEffect, useState } from "react";
import { Plus, Send, Trash2, Users, Mail, Phone, CheckCircle2, Megaphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CHANNELS = [{ v: "email", l: "E-mail" }, { v: "sms", l: "SMS" }, { v: "telefon", l: "Telefon" }, { v: "wizyta", l: "Wizyta" }, { v: "inne", l: "Inne" }];
const STATUS_BADGE = { draft: { l: "Szkic", c: "bg-slate-500/20 text-slate-300" }, active: { l: "W toku", c: "bg-sky-500/20 text-sky-300" }, done: { l: "Zakończona", c: "bg-emerald-500/20 text-emerald-300" } };
const EMPTY = { name: "", channel: "email", description: "", subject: "", body: "", cta_label: "", cta_url: "", criteria: { tags: [], crm_status: "", industry: "", region: "", no_order_days: "", only_with_email: false, audience: "b2b" } };

export const CrmCampaigns = () => {
  const [camps, setCamps] = useState([]);
  const [meta, setMeta] = useState({ tags: [], industries: [], regions: [] });
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/crm/campaigns"); setCamps(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); api.get("/crm/meta").then((r) => setMeta(r.data)).catch(() => {}); }, [load]);

  const critPayload = (c) => ({
    tags: c.tags || [], crm_status: c.crm_status || null, industry: c.industry || null,
    region: c.region || null, no_order_days: c.no_order_days ? Number(c.no_order_days) : null,
    only_with_email: !!c.only_with_email, audience: c.audience || "b2b",
  });

  const runPreview = async () => {
    try { const { data } = await api.post("/crm/segment/preview", critPayload(edit.criteria)); setPreview(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { if (edit) { setPreview(null); const t = setTimeout(runPreview, 300); return () => clearTimeout(t); } }, [edit?.criteria]);

  const save = async () => {
    if (!edit.name.trim()) return toast.error("Podaj nazwę kampanii");
    setBusy(true);
    try {
      const payload = { ...edit, criteria: critPayload(edit.criteria) };
      if (edit.id) await api.patch(`/crm/campaigns/${edit.id}`, { name: edit.name, description: edit.description, subject: edit.subject, body: edit.body, cta_label: edit.cta_label, cta_url: edit.cta_url });
      else await api.post("/crm/campaigns", payload);
      toast.success(edit.id ? "Kampania zapisana" : "Kampania utworzona"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const del = async (c) => {
    if (!(await confirm({ title: "Usunąć kampanię?", description: `Usunąć „${c.name}”?`, confirmText: "Usuń" }))) return;
    try { await api.delete(`/crm/campaigns/${c.id}`); toast.success("Usunięto"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const send = async (c) => {
    if (!(await confirm({ title: "Wysłać kampanię e-mail?", description: `Wysłać wiadomość do ${c.target_count} klientów z tej grupy? Wiadomości trafią na adresy e-mail zapisane w kartotekach.`, confirmText: "Wyślij" }))) return;
    try { const { data } = await api.post(`/crm/campaigns/${c.id}/send`); toast.success(`Wysłano: ${data.sent} · pominięto: ${data.skipped}${data.failed ? ` · błędy: ${data.failed}` : ""}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sendSmsCampaign = async (c) => {
    if (!(await confirm({ title: "Wysłać kampanię SMS?", description: `Wysłać SMS do ${c.target_count} odbiorców z tej grupy? Wiadomości trafią na numery telefonów z kartotek.`, confirmText: "Wyślij" }))) return;
    try { const { data } = await api.post(`/crm/campaigns/${c.id}/send-sms`); toast.success(`Wysłano SMS: ${data.sent}${data.failed ? ` · błędy: ${data.failed}` : ""}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const markDone = async (c) => {
    try { await api.patch(`/crm/campaigns/${c.id}`, { status: "done" }); toast.success("Oznaczono jako zakończoną"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sendTest = async () => {
    const addr = (edit.test_email || "").trim();
    if (!addr) return toast.error("Podaj adres e-mail do testu");
    setTestBusy(true);
    try {
      const { data } = await api.post("/crm/campaigns/send-test", { email: addr, subject: edit.subject, body: edit.body, cta_label: edit.cta_label, cta_url: edit.cta_url });
      toast.success(`Wysłano test na: ${data.sent_to}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setTestBusy(false); }
  };

  if (loading) return <div className="p-8 text-center font-medium text-muted-foreground">Ładowanie…</div>;
  const setCrit = (k, v) => setEdit((e) => ({ ...e, criteria: { ...e.criteria, [k]: v } }));

  return (
    <div data-testid="crm-campaigns" className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="crm-campaign-add" onClick={() => { setEdit({ ...EMPTY, criteria: { ...EMPTY.criteria } }); }} className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"><Plus className="h-4 w-4" />Nowa kampania</Button>
      </div>

      {!camps.length ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center font-semibold text-muted-foreground">Brak kampanii. Utwórz akcję marketingową i wybierz grupę klientów.</div>
      ) : (
        <div className="space-y-3">
          {camps.map((c) => {
            const sb = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
            const Ico = c.channel === "email" ? Mail : c.channel === "telefon" ? Phone : Megaphone;
            return (
              <div key={c.id} data-testid={`crm-campaign-${c.id}`} className="rounded-xl border border-border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Ico className="h-4 w-4 text-amber-400" />
                      <span className="font-display text-lg font-extrabold">{c.name}</span>
                      <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${sb.c}`}>{sb.l}</span>
                    </div>
                    {c.description && <p className="mt-1 text-sm text-muted-foreground">{c.description}</p>}
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1 font-semibold text-foreground/80"><Users className="h-3.5 w-3.5" />{c.target_count} klientów</span>
                      {c.channel === "email" && <span>Wysłano: <b className="text-foreground">{c.sent_count || 0}</b></span>}
                    {c.channel === "sms" && <span>Wysłano SMS: <b className="text-foreground">{c.sent_count || 0}</b></span>}
                    <span>Kontakt odnotowany: <b className="text-foreground">{(c.contacted_ids || []).length}</b></span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    {c.channel === "email" && c.status !== "done" && (
                      <Button data-testid={`crm-campaign-send-${c.id}`} size="sm" className="gap-2 bg-sky-500 text-white hover:bg-sky-600 font-semibold" onClick={() => send(c)}><Send className="h-4 w-4" />Wyślij e-mail</Button>
                    )}
                    {c.channel === "sms" && c.status !== "done" && (
                      <Button data-testid={`crm-campaign-sendsms-${c.id}`} size="sm" className="gap-2 bg-sky-500 text-white hover:bg-sky-600 font-semibold" onClick={() => sendSmsCampaign(c)}><Send className="h-4 w-4" />Wyślij SMS</Button>
                    )}
                    {!["email", "sms"].includes(c.channel) && c.status !== "done" && (
                      <Button data-testid={`crm-campaign-done-${c.id}`} size="sm" variant="outline" className="gap-2 font-semibold" onClick={() => markDone(c)}><CheckCircle2 className="h-4 w-4" />Zakończ</Button>
                    )}
                    <Button data-testid={`crm-campaign-edit-${c.id}`} size="sm" variant="outline" className="font-semibold" onClick={() => setEdit({ ...c, criteria: { ...EMPTY.criteria, ...(c.criteria || {}), no_order_days: c.criteria?.no_order_days || "" } })}>Edytuj</Button>
                    <Button data-testid={`crm-campaign-del-${c.id}`} size="icon" variant="ghost" className="h-9 w-9 text-rose-400" onClick={() => del(c)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="crm-campaign-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">{edit?.id ? "Edytuj kampanię" : "Nowa kampania"}</DialogTitle></DialogHeader>
          {edit && <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Nazwa *</Label><Input data-testid="crm-campaign-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="mt-1" placeholder="np. Promocja M18 — październik" /></div>
              <div><Label className="text-xs">Kanał</Label>
                <Select value={edit.channel} onValueChange={(v) => setEdit({ ...edit, channel: v })} disabled={!!edit.id}>
                  <SelectTrigger data-testid="crm-campaign-channel" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CHANNELS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Opis (wewnętrzny)</Label><Input data-testid="crm-campaign-desc" value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="mt-1" /></div>

            {edit.channel === "sms" && (
              <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-2">
                <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-amber-400/90">Treść SMS (nadawca: EMPRO)</p>
                <Textarea data-testid="crm-campaign-sms-body" value={edit.body || ""} onChange={(e) => setEdit({ ...edit, body: e.target.value })} className="mt-1" rows={4} maxLength={640} placeholder="np. EMPRO: nowa dostawa Milwaukee M18! Sprawdź ofertę u swojego handlowca." />
                <p className="text-[11px] text-muted-foreground">{(edit.body || "").length} znaków · polskie znaki skracają limit pojedynczego SMS do 70 (dłuższe = kilka SMS-ów).</p>
              </div>
            )}

            {edit.channel === "email" && (
              <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-3">
                <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-amber-400/90">Treść e-maila</p>
                <div><Label className="text-xs">Temat</Label><Input data-testid="crm-campaign-subject" value={edit.subject || ""} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Treść</Label><Textarea data-testid="crm-campaign-body" value={edit.body || ""} onChange={(e) => setEdit({ ...edit, body: e.target.value })} className="mt-1" rows={5} placeholder="Każdy nowy wiersz = osobny akapit." /></div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Przycisk — tekst (opcj.)</Label><Input data-testid="crm-campaign-cta-label" value={edit.cta_label || ""} onChange={(e) => setEdit({ ...edit, cta_label: e.target.value })} className="mt-1" placeholder="np. Zobacz promocję" /></div>
                  <div><Label className="text-xs">Przycisk — link (https)</Label><Input data-testid="crm-campaign-cta-url" value={edit.cta_url || ""} onChange={(e) => setEdit({ ...edit, cta_url: e.target.value })} className="mt-1" placeholder="https://empro.pro/promocje/" /></div>
                </div>
                <div className="mt-1 flex flex-col sm:flex-row sm:items-end gap-2 rounded-md border border-dashed border-sky-500/40 bg-sky-500/5 p-2.5">
                  <div className="flex-1"><Label className="text-xs">Adres do testu</Label><Input data-testid="crm-campaign-test-email" type="email" value={edit.test_email || ""} onChange={(e) => setEdit({ ...edit, test_email: e.target.value })} className="mt-1" placeholder="twoj@adres.pl" /></div>
                  <Button type="button" data-testid="crm-campaign-test-send" variant="outline" disabled={testBusy} onClick={sendTest} className="gap-2 font-semibold border-sky-500/50 text-sky-300 hover:bg-sky-500/10">{testBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Wyślij test</Button>
                </div>
              </div>
            )}

            {!edit.id && (
              <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-3" data-testid="crm-campaign-segment">
                <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-amber-400/90">Grupa docelowa (segment)</p>
                <div>
                  <Label className="text-xs">Baza adresowa</Label>
                  <Select value={edit.criteria.audience || "b2b"} onValueChange={(v) => setCrit("audience", v)}>
                    <SelectTrigger data-testid="crm-seg-audience" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="b2b">Kartoteka B2B (firmy handlowców)</SelectItem>
                      <SelectItem value="shop">Klienci sklepu B2C (kupujący + konta)</SelectItem>
                      <SelectItem value="all">Obie bazy razem (B2B + sklep)</SelectItem>
                    </SelectContent>
                  </Select>
                  {edit.criteria.audience !== "b2b" && <p className="mt-1 text-[11px] text-muted-foreground">Dla klientów sklepu kryteria B2B (tagi/status/branża/region) nie są stosowane — dołączani są wszyscy kupujący i zarejestrowani z adresem e-mail.</p>}
                </div>
                {edit.criteria.audience !== "shop" && (<>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Status współpracy</Label>
                    <Select value={edit.criteria.crm_status || "any"} onValueChange={(v) => setCrit("crm_status", v === "any" ? "" : v)}>
                      <SelectTrigger data-testid="crm-seg-status" className="mt-1"><SelectValue placeholder="Dowolny" /></SelectTrigger>
                      <SelectContent><SelectItem value="any">Dowolny</SelectItem><SelectItem value="lead">Lead</SelectItem><SelectItem value="aktywny">Aktywny</SelectItem><SelectItem value="nieaktywny">Nieaktywny</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Branża</Label>
                    <Select value={edit.criteria.industry || "any"} onValueChange={(v) => setCrit("industry", v === "any" ? "" : v)}>
                      <SelectTrigger data-testid="crm-seg-industry" className="mt-1"><SelectValue placeholder="Dowolna" /></SelectTrigger>
                      <SelectContent><SelectItem value="any">Dowolna</SelectItem>{meta.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Region</Label>
                    <Select value={edit.criteria.region || "any"} onValueChange={(v) => setCrit("region", v === "any" ? "" : v)}>
                      <SelectTrigger data-testid="crm-seg-region" className="mt-1"><SelectValue placeholder="Dowolny" /></SelectTrigger>
                      <SelectContent><SelectItem value="any">Dowolny</SelectItem>{meta.regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Bez zamówienia od (dni)</Label><Input data-testid="crm-seg-noorder" type="number" min="0" value={edit.criteria.no_order_days || ""} onChange={(e) => setCrit("no_order_days", e.target.value)} className="mt-1" placeholder="np. 90" /></div>
                </div>
                {meta.tags.length > 0 && (
                  <div>
                    <Label className="text-xs">Tagi (dowolny z zaznaczonych)</Label>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {meta.tags.map((t) => {
                        const on = (edit.criteria.tags || []).includes(t);
                        return <button key={t} type="button" data-testid={`crm-seg-tag-${t}`} onClick={() => setCrit("tags", on ? edit.criteria.tags.filter((x) => x !== t) : [...(edit.criteria.tags || []), t])} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? "border-amber-400 bg-amber-400/15 text-amber-300" : "border-border text-muted-foreground hover:border-amber-400/50"}`}>{t}</button>;
                      })}
                    </div>
                  </div>
                )}
                </>)}
                {edit.channel === "email" && (
                  <label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground"><input type="checkbox" data-testid="crm-seg-withemail" checked={!!edit.criteria.only_with_email} onChange={(e) => setCrit("only_with_email", e.target.checked)} /> Tylko klienci z adresem e-mail</label>
                )}
                <div className="rounded-md bg-background px-3 py-2 text-sm font-semibold" data-testid="crm-seg-preview">
                  {preview ? <>
                    {(edit.criteria.audience || "b2b") !== "shop" && <>B2B: <span className="text-amber-300">{preview.b2b_count ?? preview.count}</span> {edit.channel === "sms" ? `(z nr tel. ${preview.b2b_with_phone ?? 0})` : `(z e-mailem ${preview.b2b_with_email ?? preview.with_email})`} </>}
                    {(edit.criteria.audience || "b2b") !== "b2b" && <>· Sklep: <span className="text-amber-300">{preview.shop_count ?? 0}</span> </>}
                    · <span className="text-sky-300">Łącznie do wysyłki: {edit.channel === "sms" ? (preview.sms_total ?? 0) : (preview.mailing_total ?? preview.with_email)}</span>
                  </> : "Liczenie grupy…"}
                </div>
              </div>
            )}
          </div>}
          <DialogFooter>
            <Button data-testid="crm-campaign-save" onClick={save} disabled={busy} className="bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">{busy ? "Zapisywanie…" : edit?.id ? "Zapisz" : "Utwórz kampanię"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
